/**
 * Expression keyboard — the pure engine.
 *
 * One text field, typed into or filled by tapping keys. Everything that turns
 * that text into C, into plain words and into problems lives here, with no
 * React and no globals: every function takes the environment it needs.
 *
 *   env = createEnv({
 *     variables: [{ name, type, fields?: [{ name, type }], arrow? }],
 *     constants: [{ name, type? }],
 *     functions: { name: { params?: [names], variadic?, minParams?, group?, words? } },
 *     types:     ['Student', ...],        // extra type names a cast may use
 *   })
 *
 * The pipeline:
 *   text  --tokenize-->  tokens  --parse-->  tree  --emit-->  C
 *                                              \--say-->   words
 *   tokens --serialize--> the field's text (maths symbols: × ÷ − ≠ ≤ ≥ mod and or not)
 *
 * The C the surfaces store is emit(tree): what codegen already reads.
 *
 * Ported from the approved prototype (expression-builder-prototype.html) and
 * extended so the course's real expressions round-trip: member access
 * (rec.name, p->next, tab[i].name), char escapes, hex and decimal numbers,
 * casts to any C scalar type, sizeof x / sizeof(struct X), bitwise operators.
 */

// ── operators ────────────────────────────────────────────────────────────────
// The keys show maths; the C line shows what C writes.
export const OP = {
  '+': { math: '+', words: 'plus' },
  '-': { math: '−', words: 'minus' },
  '*': { math: '×', words: 'times' },
  '/': { math: '÷', words: 'divided by' },
  '%': { math: 'mod', words: 'remainder after dividing by' },
  '==': { math: '=', words: 'is' },
  '!=': { math: '≠', words: 'is not' },
  '<': { math: '<', words: 'is less than' },
  '>': { math: '>', words: 'is more than' },
  '<=': { math: '≤', words: 'is at most' },
  '>=': { math: '≥', words: 'is at least' },
  '&&': { math: 'and', words: 'and', word: true },
  '||': { math: 'or', words: 'or', word: true },
  '&': { math: '&', words: 'bitwise and' },
  '|': { math: '|', words: 'bitwise or' },
  '^': { math: '^', words: 'bitwise exclusive or' },
  '<<': { math: '<<', words: 'shifted left by' },
  '>>': { math: '>>', words: 'shifted right by' },
};

export const PREC = {
  '||': 3, '&&': 4, '|': 5, '^': 6, '&': 7,
  '==': 8, '!=': 8, '<': 9, '<=': 9, '>': 9, '>=': 9,
  '<<': 10, '>>': 10, '+': 11, '-': 11, '*': 12, '/': 12, '%': 12,
};

// [C spelling inside the quotes, key label, words]
export const CHARS = [
  ['\\0', 'end of text', 'the end of the text'],
  [';', ';', 'a ;'],
  ["\\'", 'quote', 'a quote'],
  [' ', 'space', 'a space'],
  ['\\n', 'new line', 'a new line'],
  ['\\t', 'tab', 'a tab'],
  ['\\\\', 'backslash', 'a backslash'],
];

// The More drawer's conversions, and how any cast reads.
export const CASTS = { double: 'a decimal number', int: 'a whole number' };
const CAST_WORDS = {
  double: 'a decimal number', float: 'a decimal number', 'long double': 'a decimal number',
  int: 'a whole number', long: 'a whole number', 'long long': 'a whole number', short: 'a whole number',
  'unsigned int': 'a whole number', unsigned: 'a whole number', 'unsigned long': 'a whole number', size_t: 'a whole number',
  char: 'a character', 'unsigned char': 'a character code', 'signed char': 'a character',
};

// Identifiers that are values in every C program.
const BUILTIN_CONSTANTS = [
  'NULL', 'EOF', 'true', 'false', 'stdin', 'stdout', 'stderr', 'RAND_MAX',
  'INT_MAX', 'INT_MIN', 'LONG_MAX', 'LONG_MIN', 'CHAR_MAX', 'CHAR_MIN', 'UINT_MAX',
  'SEEK_SET', 'SEEK_CUR', 'SEEK_END', 'EXIT_SUCCESS', 'EXIT_FAILURE', 'BUFSIZ',
];

const FOPEN_HOW = {
  r: 'to read', w: 'to write, empty', a: 'to add to the end', rb: 'to read bytes', wb: 'to write bytes, empty',
  ab: 'to add bytes to the end', 'r+': 'to read and change', 'w+': 'to write and read, empty', 'a+': 'to add to and read',
};
const SEEK_WORDS = { SEEK_SET: 'from the start', SEEK_CUR: 'on from where it is', SEEK_END: 'from the end' };

// Words for the C library calls students meet. Anything else reads as
// "<name> of <values>".
const FN_WORDS = {
  sqrt: (a) => `the square root of ${a[0]}`,
  pow: (a) => `${a[0]} to the power ${a[1]}`,
  fabs: (a) => `${a[0]} without its sign`,
  abs: (a) => `${a[0]} without its sign`,
  floor: (a) => `${a[0]} rounded down`,
  ceil: (a) => `${a[0]} rounded up`,
  round: (a) => `${a[0]} rounded`,
  strlen: (a) => `the length of ${a[0]}`,
  strcmp: (a) => `the comparison of ${a[0]} with ${a[1]}`,
  strncmp: (a) => `the comparison of the first ${a[2]} characters of ${a[0]} with ${a[1]}`,
  strcpy: (a) => `copy ${a[1]} into ${a[0]}`,
  strncpy: (a) => `copy at most ${a[2]} characters of ${a[1]} into ${a[0]}`,
  strcat: (a) => `add ${a[1]} to the end of ${a[0]}`,
  strncat: (a) => `add ${a[1]} to the end of ${a[0]}, at most ${a[2]} characters`,
  strchr: (a) => `where ${a[1]} first appears in ${a[0]}`,
  strstr: (a) => `where ${a[1]} first appears in ${a[0]}`,
  tolower: (a) => `the small letter of ${a[0]}`,
  toupper: (a) => `the capital of ${a[0]}`,
  isdigit: (a) => `${a[0]} is a digit`,
  isalpha: (a) => `${a[0]} is a letter`,
  isalnum: (a) => `${a[0]} is a letter or a digit`,
  isspace: (a) => `${a[0]} is a space`,
  isupper: (a) => `${a[0]} is a capital`,
  islower: (a) => `${a[0]} is a small letter`,
  ispunct: (a) => `${a[0]} is a punctuation mark`,
  atoi: (a) => `the whole number written in ${a[0]}`,
  atof: (a) => `the decimal number written in ${a[0]}`,
  rand: () => 'a random number',
  snprintf: (a) => `write ${a.slice(2).join(' with ')} into ${a[0]}, at most ${a[1]} long`,
  // Do v2 (Ram, 2026-10-24): the jobs production Do steps call most. A job that
  // gives back a value reads as that value ("the file … opened to read"); one
  // that changes something reads as what it does ("close the file fp").
  sprintf: (a) => `write ${a.slice(1).join(' with ')} into ${a[0]}`,
  sscanf: (a) => `read ${a.slice(1).join(' with ')} from ${a[0]}`,
  strtok: (a) => `the next piece of ${a[0]}, split at ${a[1]}`,
  strpbrk: (a) => `where any of ${a[1]} first appears in ${a[0]}`,
  fopen: (a) => `the file ${a[0]} opened ${FOPEN_HOW[String(a[1] || '').replace(/^"|"$/g, '')] || `with ${a[1]}`}`,
  fclose: (a) => `close the file ${a[0]}`,
  fprintf: (a) => `write ${a.slice(1).join(' with ')} to the file ${a[0]}`,
  fscanf: (a) => `read ${a.slice(1).join(' with ')} from the file ${a[0]}`,
  fputs: (a) => `write ${a[0]} to the file ${a[1]}`,
  fputc: (a) => `write the character ${a[0]} to the file ${a[1]}`,
  fgets: (a) => `read a line of at most ${a[1]} characters from the file ${a[2]} into ${a[0]}`,
  fgetc: (a) => `the next character of the file ${a[0]}`,
  fwrite: (a) => `write ${a[2]} items of size ${a[1]} from ${a[0]} to the file ${a[3]}`,
  fread: (a) => `read ${a[2]} items of size ${a[1]} from the file ${a[3]} into ${a[0]}`,
  feof: (a) => `the file ${a[0]} is at its end`,
  fseek: (a) => `move ${a[1]} bytes ${SEEK_WORDS[a[2]] || `from ${a[2]}`} in the file ${a[0]}`,
  ftell: (a) => `where the file ${a[0]} is now`,
  rewind: (a) => `go back to the start of the file ${a[0]}`,
  remove: (a) => `delete the file ${a[0]}`,
  rename: (a) => `rename the file ${a[0]} to ${a[1]}`,
  malloc: (a) => `new memory of ${a[0]} bytes`,
  calloc: (a) => `new memory for ${a[0]} items of size ${a[1]}, all zero`,
  realloc: (a) => `${a[0]} resized to ${a[1]} bytes`,
  free: (a) => `give back the memory at ${a[0]}`,
  memcpy: (a) => `copy ${a[2]} bytes from ${a[1]} into ${a[0]}`,
  memset: (a) => `fill ${a[2]} bytes of ${a[0]} with ${a[1]}`,
};

/** A library call in words, from its values already in words; null when it has no words. */
export function callWordsFor(fn, args = []) {
  const words = Object.prototype.hasOwnProperty.call(FN_WORDS, fn) ? FN_WORDS[fn] : null;
  if (!words) return null;
  try { return words(args); } catch (e) { return null; }
}

/** Does this library call have words of its own? */
export const hasCallWords = (fn) => Object.prototype.hasOwnProperty.call(FN_WORDS, fn);

// ── environment ─────────────────────────────────────────────────────────────
export function createEnv({ variables = [], constants = [], functions = {}, types = [] } = {}) {
  const vars = new Map();
  variables.forEach((v) => { if (v && v.name && !vars.has(v.name)) vars.set(v.name, v); });
  const consts = new Map();
  BUILTIN_CONSTANTS.forEach((n) => consts.set(n, { name: n, builtin: true }));
  constants.forEach((c) => { if (c && c.name) consts.set(c.name, c); });
  return { vars, consts, functions: functions || {}, types: new Set(types || []), variables, constants };
}

const isKnownValue = (env, name) => env.vars.has(name) || env.consts.has(name);
const fnInfo = (env, name) => (env.functions && Object.prototype.hasOwnProperty.call(env.functions, name) ? env.functions[name] : null);

// ── tokens ──────────────────────────────────────────────────────────────────
// {k:'var'|'num'|'chr'|'str'|'op'|'('|')'|'['|']'|','|'?'|':'|'fn'|'cast'|'type'
//    |'not'|'bitnot'|'addr'|'deref'|'sizeof'|'if'|'member'|'bad', v}
// A finished value: what may come right before an operator.
const VALUE_END = ['var', 'num', 'chr', 'str', ')', ']', 'member'];

const BASE_TYPE = '(?:(?:unsigned|signed|const)\\s+)*(?:long\\s+long|long\\s+double|char|short|int|long|float|double|size_t|unsigned|signed|_Bool|bool|void|FILE|(?:struct|union|enum)\\s+[A-Za-z_]\\w*)(?:\\s+int)?';
function typeRegex(env) {
  const extra = [...(env.types || [])].filter((t) => /^[A-Za-z_]\w*$/.test(t));
  const alt = extra.length ? `(?:${BASE_TYPE}|${extra.join('|')})` : `(?:${BASE_TYPE})`;
  return new RegExp(`^\\(\\s*(${alt}(?:\\s*\\*)*)\\s*\\)`);
}
const tidyType = (t) => t.replace(/\s+/g, ' ').replace(/\s*\*\s*/g, '*').replace(/([^*\s])\*/, '$1 *').trim();

const WORDS_OPS = { and: { k: 'op', v: '&&' }, or: { k: 'op', v: '||' }, mod: { k: 'op', v: '%' }, not: { k: 'not' }, if: { k: 'if' }, then: { k: '?' }, otherwise: { k: ':' } };

export function tokenize(src, env = createEnv()) {
  const out = [];
  const s = String(src || '');
  const TYPE_RE = typeRegex(env);
  let j = 0;
  const prevEndsValue = () => { const p = out[out.length - 1]; return !!p && VALUE_END.includes(p.k); };
  while (j < s.length) {
    const ch = s[j];
    if (/\s/.test(ch)) { j += 1; continue; }
    const two = s.slice(j, j + 2);
    if (two === '->' && prevEndsValue()) {
      const m = s.slice(j + 2).match(/^\s*([A-Za-z_]\w*)?/);
      out.push({ k: 'member', v: m[1] || '', arrow: true }); j += 2 + m[0].length; continue;
    }
    if (['==', '!=', '<=', '>=', '&&', '||', '<<', '>>'].includes(two)) { out.push({ k: 'op', v: two }); j += 2; continue; }
    if ('≠≤≥×÷−'.includes(ch)) { out.push({ k: 'op', v: { '≠': '!=', '≤': '<=', '≥': '>=', '×': '*', '÷': '/', '−': '-' }[ch] }); j += 1; continue; }
    if ('+-/%<>^|'.includes(ch)) { out.push({ k: 'op', v: ch }); j += 1; continue; }
    if (ch === '*') { out.push(prevEndsValue() ? { k: 'op', v: '*' } : { k: 'deref' }); j += 1; continue; }
    if (ch === '&') { out.push(prevEndsValue() ? { k: 'op', v: '&' } : { k: 'addr' }); j += 1; continue; }
    if (ch === '=') { out.push({ k: 'op', v: '==' }); j += 1; continue; }
    if (ch === '!') { out.push({ k: 'not' }); j += 1; continue; }
    if (ch === '~') { out.push({ k: 'bitnot' }); j += 1; continue; }
    if (ch === '.' && prevEndsValue() && !/\d/.test(s[j + 1] || '')) {
      const m = s.slice(j + 1).match(/^\s*([A-Za-z_]\w*)?/);
      out.push({ k: 'member', v: m[1] || '', arrow: false }); j += 1 + m[0].length; continue;
    }
    if (ch === '(') {
      const m = s.slice(j).match(TYPE_RE);
      if (m) {
        const prev = out[out.length - 1];
        if (prev && prev.k === 'sizeof') { out.push({ k: '(' }, { k: 'type', v: tidyType(m[1]) }, { k: ')' }); } else out.push({ k: 'cast', v: tidyType(m[1]) });
        j += m[0].length; continue;
      }
    }
    if ('()[],?:'.includes(ch)) { out.push({ k: ch }); j += 1; continue; }
    if (ch === "'") {
      const m = s.slice(j).match(/^'(\\(?:x[0-9a-fA-F]+|[0-7]{1,3}|.)|[^'\\])'/);
      if (m) { out.push({ k: 'chr', v: m[1] }); j += m[0].length; continue; }
      // Still being typed: keep what is there, unclosed.
      out.push({ k: 'chr', v: s.slice(j + 1), open: true }); j = s.length; continue;
    }
    if (ch === '"') {
      const m = s.slice(j).match(/^"((?:\\.|[^"\\])*)"/);
      if (m) { out.push({ k: 'str', v: m[1] }); j += m[0].length; continue; }
      out.push({ k: 'str', v: s.slice(j + 1), open: true }); j = s.length; continue;
    }
    if (/\d/.test(ch) || (ch === '.' && /\d/.test(s[j + 1] || ''))) {
      const m = s.slice(j).match(/^(?:0[xX][0-9a-fA-F]+|\d+\.?\d*(?:[eE][+-]?\d+)?|\.\d+(?:[eE][+-]?\d+)?)[uUlLfF]*/);
      out.push({ k: 'num', v: m[0] }); j += m[0].length; continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      const w = s.slice(j).match(/^[A-Za-z_]\w*/)[0];
      j += w.length;
      const callFollows = /^\s*\(/.test(s.slice(j));
      if (w === 'sizeof') out.push({ k: 'sizeof' });
      else if (WORDS_OPS[w] && !isKnownValue(env, w)) out.push({ ...WORDS_OPS[w] });
      else if (callFollows) out.push({ k: 'fn', v: w });
      else out.push({ k: 'var', v: w });
      continue;
    }
    out.push({ k: 'bad', v: ch }); j += 1;
  }
  return out;
}

// ── tokens → the text in the field (as the keys show it), and back ──────────
const MATHTXT = { '*': '×', '/': '÷', '-': '−', '%': 'mod', '==': '=', '!=': '≠', '<=': '≤', '>=': '≥', '&&': 'and', '||': 'or' };
const UNARY_BEFORE = ['op', '(', '[', ',', '?', ':', 'not', 'if', 'bitnot', 'addr', 'deref', 'cast', 'sizeof'];
const isUnaryMinus = (toks, j) => toks[j].k === 'op' && toks[j].v === '-' && (j === 0 || UNARY_BEFORE.includes(toks[j - 1].k));

function fieldWord(t) {
  switch (t.k) {
    case 'var': case 'num': case 'fn': case 'bad': case 'type': return t.v;
    case 'chr': return t.open ? `'${t.v}` : `'${t.v}'`;
    case 'str': return t.open ? `"${t.v}` : `"${t.v}"`;
    case 'op': return MATHTXT[t.v] || t.v;
    case 'cast': return `(${t.v})`;
    case 'not': return 'not';
    case 'bitnot': return '~';
    case 'addr': return '&';
    case 'deref': return '*';
    case 'sizeof': return 'sizeof';
    case 'if': return 'if';
    case '?': return 'then';
    case ':': return 'otherwise';
    case 'member': return `${t.arrow ? '->' : '.'}${t.v}`;
    default: return t.k;
  }
}

export function serialize(toks) {
  let text = '';
  const at = [0];
  toks.forEach((t, j) => {
    const prev = toks[j - 1];
    const tight = !prev
      || ['(', '[', 'fn', 'addr', 'deref', 'cast', 'bitnot'].includes(prev.k)
      || [')', ']', ',', '[', 'member'].includes(t.k)
      || (t.k === '(' && (prev.k === 'fn' || prev.k === 'sizeof'))
      || (prev.k === 'op' && prev.v === '-' && isUnaryMinus(toks, j - 1));
    if (!tight) text += ' ';
    text += isUnaryMinus(toks, j) ? '−' : fieldWord(t);
    at.push(text.length);
  });
  return { text, at };
}

// The C spelling of a token stream, even one that does not parse yet — so a
// half-written expression is kept, not lost, when the window closes.
function cWord(t) {
  switch (t.k) {
    case 'var': case 'num': case 'fn': case 'bad': case 'type': return t.v;
    case 'chr': return t.open ? `'${t.v}` : `'${t.v}'`;
    case 'str': return t.open ? `"${t.v}` : `"${t.v}"`;
    case 'op': return t.v;
    case 'cast': return `(${t.v})`;
    case 'not': return '!';
    case 'bitnot': return '~';
    case 'addr': return '&';
    case 'deref': return '*';
    case 'sizeof': return 'sizeof';
    case 'if': return '';
    case 'member': return `${t.arrow ? '->' : '.'}${t.v}`;
    default: return t.k;
  }
}
export function tokensToC(toks) {
  let text = '';
  toks.forEach((t, j) => {
    const w = cWord(t);
    if (!w) return;
    const prev = toks[j - 1];
    const tight = !text
      || (prev && ['(', '[', 'fn', 'addr', 'deref', 'cast', 'not', 'bitnot', 'if'].includes(prev.k))
      || [')', ']', ',', '[', 'member'].includes(t.k)
      || (t.k === '(' && prev && (prev.k === 'fn' || prev.k === 'sizeof'))
      || (prev && prev.k === 'op' && prev.v === '-' && isUnaryMinus(toks, j - 1));
    text += (tight ? '' : ' ') + w;
  });
  return text;
}

// ── parse: tokens → tree, or the first problem in plain words ───────────────
const show = (t) => {
  if (!t) return '';
  if (t.k === 'op') return OP[t.v].math;
  if (['var', 'num', 'fn', 'type'].includes(t.k)) return t.v;
  if (t.k === 'member') return `${t.arrow ? '->' : '.'}${t.v}`;
  return fieldWord(t);
};
const paramsOf = (info) => (info && Array.isArray(info.params) ? info.params : null);

export function parse(toks, env = createEnv(), { sequence = false } = {}) {
  let p = 0;
  const needIf = []; // where a choice was written without its 'if'
  const peek = () => toks[p];
  const fail = (msg, at) => { const e = new Error(msg); e.at = at; e.isProblem = true; throw e; };

  function call(t) {
    const at = p; p += 1;
    if (!peek() || peek().k !== '(') fail(`${t.v} needs its brackets.`, at);
    p += 1;
    const info = fnInfo(env, t.v);
    if (!info && !env.vars.has(t.v)) fail(`There is no function called “${t.v}”.`, at);
    const params = paramsOf(info);
    const args = [];
    if (peek() && peek().k === ')') {
      p += 1;
    } else {
      for (;;) {
        if (!peek()) fail(`The bracket after ${t.v} is not closed.`, at);
        args.push(expr(0));
        if (peek() && peek().k === ',') { p += 1; continue; }
        if (peek() && peek().k === ')') { p += 1; break; }
        fail(`The bracket after ${t.v} is not closed.`, at);
      }
    }
    if (params) {
      const min = info.minParams ?? params.length;
      const max = info.variadic ? Infinity : params.length;
      if (args.length < min || args.length > max) {
        if (!args.length) fail(`${t.v} needs ${min === 1 ? 'a value' : `${min} values`}: ${params.join(', ')}.`, at);
        const want = info.variadic ? `at least ${min}` : (min === max ? `${min}` : `${min} to ${max}`);
        fail(`${t.v} takes ${want} value${max === 1 && min === 1 ? '' : 's'} (${params.join(', ')}${info.variadic ? ', …' : ''}), not ${args.length}.`, at);
      }
    }
    return { t: 'call', fn: t.v, args };
  }

  function primary() {
    const t = peek();
    if (!t) fail(p === 0 ? '' : `Something is missing at the end, after ${show(toks[p - 1])}.`, p);
    if (t.k === 'if') {
      const at = p; p += 1;
      if (!peek() || peek().k === '?') fail('What should be true? Put a condition after “if”.', at);
      const c = expr(3);
      if (!peek() || peek().k !== '?') fail('An “if” needs a “then”.', at);
      p += 1; const a = expr(3);
      if (!peek() || peek().k !== ':') fail('A “then” needs an “otherwise”.', p);
      p += 1; const b = expr(2);
      return { t: 'choose', c, a, b };
    }
    if (t.k === 'num') { p += 1; return { t: 'num', v: t.v }; }
    if (t.k === 'chr') {
      if (t.open) fail('This character is not closed: put a \' after it.', p);
      p += 1; return { t: 'chr', v: t.v };
    }
    if (t.k === 'str') {
      if (t.open) fail('This text in quotes is not closed: put a " after it.', p);
      p += 1; return { t: 'str', v: t.v };
    }
    if (t.k === 'var') {
      if (!isKnownValue(env, t.v)) fail(`There is no variable called “${t.v}”.`, p);
      p += 1; return { t: 'var', v: t.v };
    }
    if (t.k === 'fn') return call(t);
    if (t.k === '(') {
      const at = p; p += 1;
      if (peek() && peek().k === ')') fail('These brackets are empty.', at);
      if (!peek()) fail('This ( is not closed.', at);
      const x = expr(0);
      if (!peek() || peek().k !== ')') fail('This ( is not closed.', at);
      p += 1; return { t: 'group', x };
    }
    if (t.k === 'op') fail(`Something is missing before ${OP[t.v].math}.`, p);
    if (t.k === ')') fail('This ) has no ( before it.', p);
    if (t.k === ']') fail('This ] has no [ before it.', p);
    if (t.k === ',') fail('A comma only goes between the values of a function.', p);
    if (t.k === 'member') fail(`Something is missing before ${show(t)}.`, p);
    if (t.k === 'bad') fail(`“${t.v}” can't be used here.`, p);
    return fail(`Something is missing before ${show(t)}.`, p);
  }

  function unary() {
    const t = peek();
    if (t && t.k === 'not') { p += 1; return { t: 'not', x: unary() }; }
    if (t && t.k === 'bitnot') { p += 1; return { t: 'bitnot', x: unary() }; }
    if (t && t.k === 'addr') { p += 1; return { t: 'addr', x: unary() }; }
    if (t && t.k === 'deref') { p += 1; return { t: 'deref', x: unary() }; }
    if (t && t.k === 'cast') { p += 1; return { t: 'cast', ty: t.v, x: unary() }; }
    if (t && t.k === 'op' && t.v === '-') { p += 1; return { t: 'neg', x: unary() }; }
    if (t && t.k === 'sizeof') {
      const at = p; p += 1;
      if (!peek()) fail('size of what? Put a value or a type after it.', at);
      if (peek().k === '(' && toks[p + 1] && toks[p + 1].k === 'type') {
        const ty = toks[p + 1].v; p += 2;
        if (!peek() || peek().k !== ')') fail('The bracket after size of is not closed.', at);
        p += 1; return { t: 'sizeType', ty };
      }
      return { t: 'size', x: unary() };
    }
    let x = primary();
    for (;;) {
      const n = peek();
      if (n && n.k === '[') {
        const at = p; p += 1;
        if (peek() && peek().k === ']') fail('Which position? Put it inside the [ ].', at);
        if (!peek()) fail('This [ is not closed.', at);
        const idx = expr(0);
        if (!peek() || peek().k !== ']') fail('This [ is not closed.', at);
        p += 1; x = { t: 'index', x, idx }; continue;
      }
      if (n && n.k === '(' && (x.t === 'member' || x.t === 'index')) {
        // A function reached through a field: aggregates[k].step(agg, value).
        const at = p; p += 1;
        const args = [];
        if (peek() && peek().k === ')') p += 1;
        else {
          for (;;) {
            if (!peek()) fail('This ( is not closed.', at);
            args.push(expr(0));
            if (peek() && peek().k === ',') { p += 1; continue; }
            if (peek() && peek().k === ')') { p += 1; break; }
            fail('This ( is not closed.', at);
          }
        }
        x = { t: 'callx', f: x, args }; continue;
      }
      if (n && n.k === 'member') {
        if (!n.v) fail(`Which field? Put its name after the ${n.arrow ? 'arrow' : 'dot'}.`, p);
        p += 1; x = { t: 'member', x, name: n.v, arrow: !!n.arrow }; continue;
      }
      break;
    }
    return x;
  }

  function expr(min) {
    const start = p;
    let left = unary();
    for (;;) {
      const t = peek();
      if (!t) break;
      if (t.k === '?' && min <= 2) {
        if (!(toks[start] && toks[start].k === 'if')) needIf.push(start);
        p += 1;
        if (!peek()) fail('Something is missing after “then”.', p - 1);
        const a = expr(3);
        if (!peek() || peek().k !== ':') fail('A “then” needs an “otherwise”.', p);
        p += 1;
        if (!peek()) fail('Something is missing after “otherwise”.', p - 1);
        const b = expr(2);
        left = { t: 'choose', c: left, a, b };
        continue;
      }
      if (t.k !== 'op') {
        if ([')', ']', ',', ':', '?'].includes(t.k)) break;
        if (t.k === 'bad') fail(`“${t.v}” can't be used here.`, p);
        fail(`Two values in a row: put an operator between ${show(toks[p - 1])} and ${show(t)}.`, p);
      }
      const pr = PREC[t.v];
      if (pr === undefined || pr < min) break;
      p += 1;
      if (!peek() || (peek().k === 'op' && peek().v !== '-')) {
        fail(peek() ? `Something is missing between ${OP[t.v].math} and ${OP[peek().v].math}.` : `Something is missing after ${OP[t.v].math}.`, p - 1);
      }
      left = { t: 'bin', op: t.v, l: left, r: expr(pr + 1) };
    }
    return left;
  }

  if (!toks.length) return { tree: null, needIf: [] };
  try {
    let tree = expr(0);
    // An Action statement may be a comma list: `(void) k, (void) lineNo`.
    if (sequence && peek() && peek().k === ',') {
      const items = [tree];
      while (peek() && peek().k === ',') {
        p += 1;
        if (!peek()) fail('Something is missing after the comma.', p - 1);
        items.push(expr(0));
      }
      tree = { t: 'seq', items };
    }
    if (p < toks.length) {
      const t = toks[p];
      if (t.k === ')') fail('This ) has no ( before it.', p);
      if (t.k === ']') fail('This ] has no [ before it.', p);
      if (t.k === ',') fail('A comma only goes between the values of a function.', p);
      if (t.k === ':') fail('“otherwise” needs an “if … then” before it.', p);
      fail(`Something is missing before ${show(t)}.`, p);
    }
    // A call that gives back nothing is a statement on its own: allowed only as
    // a whole Action (sequence), never as a value inside an expression.
    const tops = new Set(tree.t === 'seq' ? tree.items : [tree]);
    const visit = (n) => {
      if (!n || typeof n !== 'object') return;
      if (n.t === 'call') {
        const info = fnInfo(env, n.fn);
        if (info && info.returnsNothing && !(sequence && tops.has(n))) {
          fail(`${n.fn} gives back nothing, so it can't be used as a value${sequence ? ' — put it in an Action on its own' : ''}.`, 0);
        }
      }
      ['l', 'r', 'x', 'c', 'a', 'b', 'idx', 'f'].forEach((k) => visit(n[k]));
      (n.args || []).forEach(visit);
      (n.items || []).forEach(visit);
    };
    visit(tree);
    return { tree, needIf };
  } catch (e) {
    if (!e.isProblem) throw e;
    return { err: e.message, at: e.at };
  }
}

// ── tree → C and words ──────────────────────────────────────────────────────
const UNARY_KINDS = ['cast', 'addr', 'deref', 'not', 'neg', 'bitnot', 'size', 'sizeType'];
const prec = (n) => (n.t === 'bin' ? PREC[n.op] : n.t === 'choose' ? 2 : UNARY_KINDS.includes(n.t) ? 13 : 14);
const unwrap = (n) => (n && n.t === 'group' ? unwrap(n.x) : n);

// The tree comes from the text, so its brackets are exactly the ones written:
// emit keeps them and adds none.
export function emit(n) {
  if (!n) return '';
  switch (n.t) {
    case 'num': case 'var': return n.v;
    case 'chr': return `'${n.v}'`;
    case 'str': return `"${n.v}"`;
    case 'group': return `(${emit(n.x)})`;
    case 'bin': return `${emit(n.l)} ${n.op} ${emit(n.r)}`;
    case 'index': return `${emit(n.x)}[${emit(n.idx)}]`;
    case 'member': return `${emit(n.x)}${n.arrow ? '->' : '.'}${n.name}`;
    case 'cast': return `(${n.ty})${emit(n.x)}`;
    case 'addr': { const s = emit(n.x); return `&${s.startsWith('&') ? ' ' : ''}${s}`; }
    case 'deref': return `*${emit(n.x)}`;
    case 'not': return `!${emit(n.x)}`;
    case 'bitnot': return `~${emit(n.x)}`;
    case 'neg': { const s = emit(n.x); return `-${s.startsWith('-') ? ' ' : ''}${s}`; }
    case 'size': return n.x.t === 'group' ? `sizeof${emit(n.x)}` : `sizeof ${emit(n.x)}`;
    case 'sizeType': return `sizeof(${n.ty})`;
    case 'choose': return `${emit(n.c)} ? ${emit(n.a)} : ${emit(n.b)}`;
    case 'call': return `${n.fn}(${n.args.map((a) => emit(a)).join(', ')})`;
    case 'callx': return `${emit(n.f)}(${n.args.map((a) => emit(a)).join(', ')})`;
    case 'seq': return n.items.map((x) => emit(x)).join(', ');
    default: return '';
  }
}

const isTextType = (type) => /char\s*(\[|\*)/.test(type || '') || /^(char\[\]|string|str|text)$/i.test(type || '');
const charWords = (v) => (CHARS.find((c) => c[0] === v) || [])[2] || `'${v}'`;
const typeWords = (ty) => CAST_WORDS[ty] || `a ${ty.replace(/^struct /, '')}`;

export function say(n0, env = createEnv(), ctx = 0) {
  const n = unwrap(n0);
  if (!n) return '';
  let s;
  switch (n.t) {
    case 'num': case 'var': s = n.v; break;
    case 'chr': s = charWords(n.v); break;
    case 'str': s = `"${n.v}"`; break;
    case 'bin': s = n.op === '&&' || n.op === '||'
      ? `${say(n.l, env, PREC[n.op])}, ${OP[n.op].words} ${say(n.r, env, PREC[n.op] + 1)}`
      : `${say(n.l, env, PREC[n.op])} ${OP[n.op].words} ${say(n.r, env, PREC[n.op] + 1)}`; break;
    case 'index': {
      const base = unwrap(n.x);
      const v = base.t === 'var' ? env.vars.get(base.v) : null;
      s = v && isTextType(v.type) ? `the character of ${base.v} at ${say(n.idx, env)}` : `item ${say(n.idx, env)} of ${say(n.x, env, 14)}`;
      break;
    }
    case 'member': s = `the ${n.name} of ${say(n.x, env, 14)}`; break;
    case 'cast': s = n.ty === 'void' ? `leave ${say(n.x, env, 13)} unused` : `${say(n.x, env, 13)} as ${typeWords(n.ty)}`; break;
    case 'addr': s = `the address of ${say(n.x, env, 13)}`; break;
    case 'deref': s = `the value at ${say(n.x, env, 13)}`; break;
    case 'size': s = `the size of ${say(n.x, env, 13)}`; break;
    case 'sizeType': s = `the size of ${typeWords(n.ty).replace(/^an? /, 'a ')}`; break;
    case 'not': s = `not ${say(n.x, env, 13)}`; break;
    case 'bitnot': s = `the bits of ${say(n.x, env, 13)} flipped`; break;
    case 'neg': s = unwrap(n.x).t === 'num' ? `-${unwrap(n.x).v}` : `minus ${say(n.x, env, 13)}`; break;
    case 'choose': s = `if ${say(n.c, env, 3)}, then ${say(n.a, env, 3)}, otherwise ${say(n.b, env, 2)}`; break;
    case 'call': {
      const args = n.args.map((a) => say(a, env));
      const info = fnInfo(env, n.fn);
      const words = (info && typeof info.words === 'function' && info.words) || FN_WORDS[n.fn];
      if (words && (!info || !info.params || args.length >= (info.minParams ?? info.params.length))) {
        try { s = words(args); } catch (e) { s = null; }
      }
      if (!s) s = args.length ? `${n.fn} of ${args.join(', ')}` : n.fn;
      break;
    }
    case 'seq': s = n.items.map((x) => say(x, env)).join('; then '); break;
    case 'callx': {
      const args = n.args.map((a) => say(a, env));
      s = `${say(n.f, env, 14)}${args.length ? ` of ${args.join(', ')}` : ''}`;
      break;
    }
    default: s = '?';
  }
  return prec(n) < ctx ? `(${s})` : s;
}

// ── a number where a yes/no is needed (valid C, almost always a slip) ───────
const ARITH = ['+', '-', '*', '/', '%'];
const mathOf = (n) => serialize(tokenize(emit(n))).text;
export function advice(tree, { condition = false } = {}) {
  const out = [];
  const isNumber = (n0) => { const n = unwrap(n0); return n.t === 'num' || (n.t === 'bin' && ARITH.includes(n.op)) || n.t === 'neg'; };
  const flag = (n) => {
    if (isNumber(n)) {
      const m = mathOf(n);
      out.push(`“${m}” is a number, not a yes/no. Did you mean ${m} = 0, or ${m} ≠ 0?`);
    }
  };
  const walk = (n0) => {
    const n = unwrap(n0);
    if (!n) return;
    if (n.t === 'bin' && (n.op === '&&' || n.op === '||')) { flag(n.l); flag(n.r); }
    if (n.t === 'not') flag(n.x);
    if (n.t === 'choose') flag(n.c);
    ['l', 'r', 'x', 'c', 'a', 'b', 'idx', 'f'].forEach((k) => n[k] && walk(n[k]));
    (n.args || []).forEach(walk);
  };
  if (tree) {
    if (condition) flag(tree);
    walk(tree);
  }
  return out;
}

// ── the call the cursor is inside, and which value of it ────────────────────
export function enclosingCall(toks, cur, env = createEnv()) {
  let depth = 0;
  let arg = 0;
  for (let j = cur - 1; j >= 0; j -= 1) {
    const t = toks[j];
    if (t.k === ')' || t.k === ']') depth += 1;
    else if (t.k === '(' || t.k === '[') {
      if (depth === 0) {
        if (t.k === '(' && toks[j - 1] && toks[j - 1].k === 'fn') {
          const fn = toks[j - 1].v;
          const info = fnInfo(env, fn);
          return { fn, arg, params: paramsOf(info) || [], variadic: !!(info && info.variadic), known: !!info };
        }
        return null;
      }
      depth -= 1;
    } else if (t.k === ',' && depth === 0) arg += 1;
  }
  return null;
}
const maxArgs = (call) => (call.variadic || !call.known ? Infinity : call.params.length);

// ── keys ────────────────────────────────────────────────────────────────────
// Inside a function, a finished value followed by the start of another means
// the next value: the comma goes in, as a calculator never asks for "next number".
const ENDS = ['var', 'num', 'chr', 'str', ')', ']', 'member'];
const startsValue = (key, prev) => ['var', 'fn', 'chr', 'str', 'sizeof', 'cast'].includes(key.t)
  || (key.t === 'digit' && !(prev && prev.k === 'num'))
  || (key.t === 'pair' && key.open === '(')
  || (key.t === 'one' && ['not', 'addr', 'deref', 'if'].includes(key.v));

/** Apply one key to { toks, cur }. Pure: returns a new model. */
export function pressKey(model, key, env = createEnv()) {
  let toks = model.toks.map((t) => ({ ...t }));
  let cur = Math.max(0, Math.min(model.cur, toks.length));
  const insert = (...ts) => { toks.splice(cur, 0, ...ts); cur += ts.length; };
  {
    const prev = toks[cur - 1];
    const call = enclosingCall(toks, cur, env);
    if (prev && call && ENDS.includes(prev.k) && startsValue(key, prev) && call.arg < maxArgs(call) - 1) insert({ k: ',' });
  }
  const prev = toks[cur - 1];
  switch (key.t) {
    case 'digit':
      if (prev && prev.k === 'num') { prev.v += key.v; break; }
      insert({ k: 'num', v: key.v }); break;
    case 'var': insert({ k: 'var', v: key.v }); break;
    case 'member': insert({ k: 'member', v: key.v, arrow: !!key.arrow }); break;
    case 'op': insert({ k: 'op', v: key.v }); break;
    case 'paren': insert({ k: key.v }); break;
    case 'pair': insert({ k: key.open }, { k: key.close }); cur -= 1; break;
    case 'fn': insert({ k: 'fn', v: key.v }, { k: '(' }, { k: ')' }); cur -= 1; break;
    case 'next': {
      const call = enclosingCall(toks, cur, env);
      if (call && call.arg < maxArgs(call) - 1) insert({ k: ',' });
      else if (toks[cur] && toks[cur].k === ')') cur += 1;
      break;
    }
    case 'chr': insert({ k: 'chr', v: key.v }); break;
    case 'str': insert({ k: 'str', v: key.v }); break;
    case 'cast': insert({ k: 'cast', v: key.v }); break;
    case 'one': insert({ k: key.v }); break; // not / addr / deref / if / ? / :
    case 'sizeof': insert({ k: 'sizeof' }, { k: '(' }, { k: ')' }); cur -= 1; break;
    case 'back':
      if (cur > 0) {
        const p = toks[cur - 1];
        if (p.k === 'num' && p.v.length > 1) p.v = p.v.slice(0, -1);
        else { toks.splice(cur - 1, 1); cur -= 1; }
      }
      break;
    case 'left': cur = Math.max(0, cur - 1); break;
    case 'right': cur = Math.min(toks.length, cur + 1); break;
    case 'clear': toks = []; cur = 0; break;
    default: break;
  }
  return { toks, cur };
}

// ── typing ──────────────────────────────────────────────────────────────────
/** The field's text and caret → { toks, cur }. */
export function fromText(text, caret, env = createEnv()) {
  const s = String(text || '');
  const c = caret == null ? s.length : caret;
  const toks = tokenize(s, env);
  return { toks, cur: Math.min(toks.length, tokenize(s.slice(0, c), env).length) };
}

/**
 * Tidy: a choice written without 'if' (typed C, or "a > b then a otherwise b")
 * gets its 'if', so the field reads as the words line does. Only once the
 * expression is complete; the caret keeps its place.
 */
export function tidy(model, env = createEnv(), opts = {}) {
  const r = parse(model.toks, env, opts);
  if (!r.needIf || !r.needIf.length) return model;
  const toks = model.toks.slice();
  let { cur } = model;
  [...r.needIf].sort((x, y) => y - x).forEach((i) => { toks.splice(i, 0, { k: 'if' }); if (i < cur) cur += 1; });
  return { toks, cur };
}

// Tidy the field as you type, but only at a natural pause: after an operator,
// bracket or comma — not in the middle of a name or number, not after a
// character that may start a two-character operator (!= <= >= == && || ->),
// and never inside unclosed quotes.
const PENDING = /[\w.'"!<>=&|\-\s]/;
export function shouldTidyAfter(ch, toks = []) {
  if (!ch || PENDING.test(ch)) return false;
  return !toks.some((t) => t.open);
}

// ── conveniences for the surfaces ───────────────────────────────────────────
/** Analyse a model: tree / problem / advice / words / C. */
export function analyse(toks, env = createEnv(), { condition = false, sequence = false } = {}) {
  const r = parse(toks, env, { sequence });
  const tree = r.tree || null;
  return {
    tree,
    err: r.err || null,
    at: r.at,
    advice: tree ? advice(tree, { condition }) : [],
    words: tree ? say(tree, env) : '',
    c: tree ? emit(tree) : tokensToC(toks),
  };
}

/** Stored C text → what the card shows (maths form, words, problem). */
export function describeC(c, env = createEnv(), opts = {}) {
  const toks = tokenize(c, env);
  const a = analyse(toks, env, opts);
  return { ...a, math: serialize(toks).text };
}

/** The struct fields a key row can offer right after the value at the cursor. */
export function fieldsAt(toks, cur, env = createEnv()) {
  const prev = toks[cur - 1];
  if (!prev) return null;
  let base = null;
  if (prev.k === 'var') base = env.vars.get(prev.v);
  else if (prev.k === ']') {
    let depth = 0;
    for (let j = cur - 1; j >= 0; j -= 1) {
      if (toks[j].k === ']') depth += 1;
      else if (toks[j].k === '[') {
        depth -= 1;
        if (depth === 0) {
          const b = toks[j - 1];
          if (b && b.k === 'var') { const v = env.vars.get(b.v); base = v && v.elementFields ? { name: v.name, fields: v.elementFields, arrow: false } : null; }
          break;
        }
      }
    }
  }
  if (!base || !Array.isArray(base.fields) || !base.fields.length) return null;
  return { of: base.name, arrow: !!base.arrow, fields: base.fields };
}

/** The C to store for a model: the parsed expression, or — while it does not
 *  parse yet — its tokens in C spelling, so nothing typed is lost. */
export function toC(toks, env = createEnv(), opts = {}) {
  const r = parse(toks, env, opts);
  return r.tree ? emit(r.tree) : tokensToC(toks);
}

/**
 * Can the keyboard open this stored text without changing what it means? Not
 * when it is a statement the keyboard does not write: an assignment (`=` is
 * "equals" on the keyboard), `+=` and friends, `++` / `--`, or several
 * statements joined by `;`. Such an Action keeps its old editor.
 */
export function keyboardCanEdit(text) {
  const bare = String(text || '').replace(/"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g, '""');
  return !/(^|[^=!<>+\-*/%&|^])=(?!=)|\+\+|--|[+\-*/%&|^]=|<<=|>>=|;/.test(bare);
}
